import { Component, type ReactNode } from 'react'
import { useWindowStore } from '../../stores/windowStore'

interface Props {
  children: ReactNode
  windowId: string
  appId: string
}

interface State {
  hasError: boolean
  error: Error | null
}

export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`App crash in ${this.props.appId}:`, error, errorInfo)
  }

  handleRestart = () => {
    const { windowId, appId } = this.props
    const store = useWindowStore.getState()
    const window = store.windows.find((w) => w.id === windowId)
    if (window) {
      store.closeWindow(windowId)
      store.openWindow(appId, window.title, window.icon)
    }
    this.setState({ hasError: false, error: null })
  }

  handleClose = () => {
    useWindowStore.getState().closeWindow(this.props.windowId)
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.icon}>:(</div>
          <div style={styles.title}>Application Error</div>
          <div style={styles.message}>
            This app has crashed unexpectedly.
          </div>
          <div style={styles.error}>
            {this.state.error?.message || 'Unknown error'}
          </div>
          <div style={styles.buttons}>
            <button style={styles.button} onClick={this.handleRestart}>
              Restart App
            </button>
            <button style={styles.buttonSecondary} onClick={this.handleClose}>
              Close
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '20px',
    fontFamily: 'var(--font-mono, monospace)',
    background: 'var(--crt-bg, #1d2021)',
    color: 'var(--crt-fg, #ebdbb2)',
    textAlign: 'center',
  },
  icon: {
    fontSize: '48px',
    marginBottom: '16px',
    color: 'var(--gruvbox-red, #fb4934)',
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '8px',
    color: 'var(--gruvbox-red, #fb4934)',
  },
  message: {
    fontSize: '14px',
    marginBottom: '12px',
    color: 'var(--crt-fg-dim, #928374)',
  },
  error: {
    fontSize: '12px',
    padding: '8px 12px',
    marginBottom: '16px',
    background: 'var(--gruvbox-bg1, #3c3836)',
    border: '1px solid var(--gruvbox-red, #fb4934)',
    borderRadius: '4px',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  buttons: {
    display: 'flex',
    gap: '8px',
  },
  button: {
    padding: '8px 16px',
    fontSize: '14px',
    fontFamily: 'inherit',
    background: 'var(--gruvbox-green, #b8bb26)',
    color: 'var(--crt-bg, #1d2021)',
    border: 'none',
    cursor: 'pointer',
  },
  buttonSecondary: {
    padding: '8px 16px',
    fontSize: '14px',
    fontFamily: 'inherit',
    background: 'transparent',
    color: 'var(--crt-fg-dim, #928374)',
    border: '1px solid var(--crt-fg-dim, #928374)',
    cursor: 'pointer',
  },
}
